import React, { useContext, useEffect, useState } from "react";
import { useAuthUser } from "./dashboard/hooks/useAuthUser";
import { fetchContentDocuments } from "../api/contentful";
import { Link, useNavigate } from "react-router-dom";
import CsaTiersFormElement from "./CsaTiersFormElement";
import { callCsaSignup, type CsaSignupPayload } from "../api/csaSignup";
import CsaPickupLocationFormElement from "./CsaPickupLocationFormElement";
import CsaPickupDayFormElement from "./CsaPickupDayFormElement";
import { ModalContext } from "./dashboard/store/ModalContext";
import { formatPhoneNumber } from "./dashboard/utils/PhoneFormatters";
import { UserPickupContext } from "./dashboard/store/UserPickupContext";
import { SeasonContext } from "./dashboard/store/SeasonContext";
import { SubscriptionsContext } from "./dashboard/store/SubscriptionsContext";
import { isTierAvailable } from "../utils/tierAvailability";

const CsaApplicationForm = () => {
  const {
    isAuthenticated,
    saveAccountProfile,
    refetchDetails,
    details,
    userId,
  } = useAuthUser();
  const [signupLoading, setSignupLoading] = useState(false);
  const { openModal, closeModal } = useContext(ModalContext);
  const { refreshPickup } = useContext(UserPickupContext);
  const { csaDeliveryPrice } = useContext(SeasonContext);
  const { subscriptions } = useContext(SubscriptionsContext);
  const [selectedSubOptions, setSelectedSubOptions] =
    useState<CsaSignupPayload>({
      subscriptionTierId: "",
      pickupLocationId: "",
      pickupDay: "",
    });
  //   const [applicationPeriod, setApplicationPeriod] = useState(true);
  const [paymentPolicyLink, setPaymentPolicyLink] = useState("");
  const [csaInformationLeafletLink, setCsaInformationLeafletLink] =
    useState("");
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  function normalizeSwedishPhoneNumber(input: string): string {
    const digitsOnly = input.replace(/\D/g, "");
    if (digitsOnly.startsWith("0046")) {
      return `46${digitsOnly.slice(4)}`;
    }
    if (digitsOnly.startsWith("0")) {
      return `46${digitsOnly.slice(1)}`;
    }
    return digitsOnly;
  }

  function isValidSwedishPhoneNumber(input: string): boolean {
    const normalized = normalizeSwedishPhoneNumber(input);
    return /^46\d{7,10}$/.test(normalized);
  }
  function loginPageLink() {
    closeModal();
    navigate("/login?redirect=csa-signup");
  }

  function createProfilePageLink() {
    closeModal();
    navigate("/signup");
  }

  function handleUpdateSelectedOptions(
    key: keyof CsaSignupPayload,
    newValue: string,
  ) {
    if (!isAuthenticated) {
      openModal({
        title: "Warning",
        body: "You must be logged in to apply.",
        buttons: [
          { text: "Go to Login page", callback: () => loginPageLink() },
          { text: "Create a profile", callback: () => createProfilePageLink() },
        ],
        showCloseButton: true,
      });
      return;
    }
    setSelectedSubOptions((prev) => {
      if (key === "pickupLocationId") {
        return { ...prev, [key]: newValue, pickupDay: "" };
      }
      return { ...prev, [key]: newValue };
    });
  }
  async function handleCsaSignup() {
    if (!isAuthenticated) {
      navigate("/login?redirect=csa-signup");
      return;
    }

    setSignupLoading(true);
    try {
      await callCsaSignup(selectedSubOptions);
      await refetchDetails();
      const body = (
        <>
          <p>
            Your signup is complete. A payment email was sent. You can also pay
            now from your account:
          </p>
          <br />
          <Link to="/dashboard/client/pay" className="btn">
            Pay now
          </Link>
        </>
      );

      openModal({
        title: "Success!",
        body: body,
        showCloseButton: true,
      });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Could not complete signup.";

      openModal({
        title: "Signup Failed",
        body: msg,
        showCloseButton: true,
      });
    } finally {
      setSignupLoading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (signupLoading) return;

    const fd = new FormData(e.currentTarget);
    const data = Object.fromEntries(fd.entries());

    if (selectedSubOptions.subscriptionTierId === "") {
      alert("Please chose a tier.");
      return;
    }
    if (
      !isTierAvailable(subscriptions, selectedSubOptions.subscriptionTierId)
    ) {
      openModal({
        title: "Tier unavailable",
        body: "This tier is full. Please choose another tier.",
        showCloseButton: true,
      });
      return;
    }
    if (selectedSubOptions.pickupLocationId === "") {
      alert("Please chose a pickup location.");
      return;
    }
    if (selectedSubOptions.pickupDay === "") {
      alert("Please chose a pickup day.");
      return;
    }

    const submittedPhoneNumber = String(data.phoneNumber ?? "").trim();
    if (!isValidSwedishPhoneNumber(submittedPhoneNumber)) {
      openModal({
        title: "Invalid phone number",
        body: "Please enter a valid Swedish phone number, for example 0701234567 or +46701234567.",
        showCloseButton: true,
      });
      return;
    }

    try {
      const phoneNumber = normalizeSwedishPhoneNumber(submittedPhoneNumber);
      await saveAccountProfile({
        phoneNumber,
      });
      await handleCsaSignup();
      if (userId) {
        await refreshPickup(userId);
      }
    } catch {
      openModal({
        title: "Warning",
        body: "Request failed. Please try again.",
        showCloseButton: true,
      });
    }
  }
  function openImportantInfoModal() {
    openModal({
      title: "Important information",
      body: "Being a CSA member is not simply about purchasing a product — it is about participating in a community-based system built on mutual trust and shared responsibility. We expect our members to understand the purpose of our CSA and to contribute in a collaborative and supportive way. When you sign up, we include you in our seasonal planning. Your commitment directly affects how much we plant and how we organize the season. For this reason, membership cannot be canceled — even if payment has not yet been made. Our CSA operates on trust, and we hope you place your trust in us just as we place ours in you.",
      showCloseButton: true,
    });
  }

  useEffect(() => {
    const loadDocuments = async () => {
      const { items: documentItems } = await fetchContentDocuments();

      const paymentPolicy = documentItems.find(
        (document) => document.fields?.title === "Payment Policy",
      );
      const csaLeaflet = documentItems.find(
        (document) => document.fields?.title === "CSA Information Leaflet",
      );
      setPaymentPolicyLink(paymentPolicy?.fields?.file?.fields?.file?.url ?? "");
      setCsaInformationLeafletLink(
        csaLeaflet?.fields?.file?.fields?.file?.url ?? "",
      );
    };

    loadDocuments();
  }, []);

  return (
    <form onSubmit={handleSubmit}>
      <div className="csa-form-info-box">
        <h2>Choose your CSA experience</h2>
        <div className="info-card">
          <p>
            * You can't change your subscription or tier during the season. But
            you can change your pickup location as you wish every week. Also if
            you can't pick up your box, you can alert us easily on your profile
            about a friend picking it up for you.
          </p>
        </div>
      </div>

      <div className=" tiers-and-prices-form-wrapper">
        <h2>Tiers and Prices</h2>

        <div className="csa-box-tiers-container">
          <CsaTiersFormElement
            onTierSelected={(tierId: string) =>
              handleUpdateSelectedOptions("subscriptionTierId", tierId)
            }
            selectedTier={selectedSubOptions.subscriptionTierId}
          />
        </div>
      </div>
      <div className="location-boxes-form-wrapper">
        <h2>Pick-up Details</h2>
        <div className="pickup-location-option-container">
          <div className="location-boxes-container">
            <CsaPickupLocationFormElement
              onLocationSelected={(pickupLocationId: string) => {
                handleUpdateSelectedOptions(
                  "pickupLocationId",
                  pickupLocationId,
                );
              }}
              selectedLocationId={selectedSubOptions.pickupLocationId}
            />
          </div>
          <div>
            <CsaPickupDayFormElement
              onDaySelected={(pickupDay: string) =>
                handleUpdateSelectedOptions("pickupDay", pickupDay)
              }
              selectedDay={selectedSubOptions.pickupDay}
              selectedLocation={selectedSubOptions.pickupLocationId}
            />
          </div>
        </div>

        <p>
          An additional delivery fee of {csaDeliveryPrice}:- is added to your
          box if you chose a location which is not Alnarp's Farm. <br />
          *Note that if Alnarp's Farm is your chosen pickup location, this
          cannot be changed later.
        </p>
      </div>
      <div className="csa-additional-info-box">
        <h3>Additional info we need from you</h3>
        <div className="input-pair csa-additional-info-input-pair">
          <label htmlFor="phone-number">Phone Number</label>
          <input
            type="tel"
            inputMode="tel"
            required
            placeholder="+46 xx xxx xx xx"
            id="phone-number"
            name="phoneNumber"
            defaultValue={
              details?.phoneNumber ? formatPhoneNumber(details.phoneNumber) : ""
            }
          />
        </div>
        <div className="input-pair">
          <input type="checkbox" required id="terms-and-agreements" />
          <label htmlFor="terms-and-agreements">
            I have read the{" "}
            <a
              className="link-btn"
              href={paymentPolicyLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              membership & payment policy
            </a>
          </label>
        </div>
        <div className="input-pair">
          <input type="checkbox" required id="csa-leaflet" />
          <label htmlFor="csa-leaflet">
            I have read the{" "}
            <a
              className="link-btn"
              href={csaInformationLeafletLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              CSA Information Leaflet
            </a>
          </label>
        </div>
        <div className="input-pair">
          <input type="checkbox" required id="important-info" />
          <label htmlFor="important-info">
            I have read the{" "}
            <button
              type="button"
              className="link-btn"
              onClick={openImportantInfoModal}
            >
              important information.
            </button>
          </label>
        </div>
        <br />
        <p>
          On submission you confirm your CSA membership for the {currentYear}{" "}
          season. Once you submit the form,{" "}
          <span className="bold">your registration is final and binding</span>.
          Once you've revieved a confirmation email, you have two weeks before
          payment is due. Instructions will be in the email. Read our policies
          for more info.
        </p>{" "}
        <button type="submit" className="btn submit-btn">
          {signupLoading ? "Submitting..." : "Submit"}
        </button>
      </div>
    </form>
  );
};

export default CsaApplicationForm;
